import { useEffect, useState } from 'react';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { ContentCopy, Download, LockOutlined, PictureAsPdfOutlined, VerifiedUserOutlined } from '@mui/icons-material';
import * as certificatesApi from '@/api/certificates';
import { toApiError } from '@/api/client';
import type { IvaCondition } from '@/api/types';
import { TILDE_BRAND } from '@/components/Brand';
import { csrWsfeManualUrl } from '@/components/CsrStep2SubmitToAfip';

export interface CsrPublicSearch {
  token?: string;
}

type PublicEnvironment = certificatesApi.PublicCsrLinkInfoResponse['environment'];

interface PublicGuide {
  title: string;
  intro: string;
  steps: string[];
  references: Array<{ src: string; alt: string; caption: string }>;
}

const PUBLIC_GUIDES: Record<PublicEnvironment, PublicGuide> = {
  Production: {
    title: 'Circuito de producción',
    intro: 'Usá Administración de Certificados Digitales y Administrador de Relaciones. Este circuito emite credenciales reales.',
    steps: [
      'Entrá a ARCA con Clave Fiscal y abrí Administración de Certificados Digitales.',
      'Elegí el CUIT representado que va a facturar.',
      'Creá un alias productivo y adjuntá el CSR descargado desde este link.',
      'Descargá el certificado .crt desde el detalle del alias.',
      'En Administrador de Relaciones, autorizá Facturación Electrónica (wsfe) para el computador fiscal del alias.',
      'Importá el certificado .crt en Tilde y validá la conexión de producción.',
    ],
    references: [
      {
        src: '/guides/arca-production/03-alias-csr.jpg',
        alt: 'Pantalla de producción para cargar alias y CSR',
        caption: 'Alias productivo y CSR adjunto',
      },
      {
        src: '/guides/arca-production/05-administrador-relaciones.png',
        alt: 'Administrador de Relaciones de producción',
        caption: 'Administrador de Relaciones',
      },
      {
        src: '/guides/arca-production/08-confirmar-relacion.png',
        alt: 'Confirmación de relación productiva',
        caption: 'Confirmación final de wsfe',
      },
    ],
  },
  Homologation: {
    title: 'Circuito de homologación',
    intro: 'Usá WSASS Testing/Homologación. No uses los menús productivos: la interfaz y las autorizaciones son distintas.',
    steps: [
      'Entrá a ARCA con Clave Fiscal, buscá “WSASS - Autogestión Certificados Homologación” y abrí el sitio de testing.',
      'En Servicios, ubicá `wsfe - Facturación Electrónica` dentro del catálogo.',
      'Abrí Nuevo Certificado, escribí un alias de testing y pegá el CSR copiado desde esta página o descargado desde este link.',
      'Copiá el bloque BEGIN CERTIFICATE que devuelve WSASS y guardalo como .crt o .pem.',
      'Entrá en Crear autorización a servicio.',
      'Seleccioná el alias, completá el CUIT representado y elegí el servicio wsfe.',
      'Revisá Autorizaciones y confirmá que exista una fila activa para ese alias + CUIT + wsfe.',
      'Importá el certificado en Tilde con entorno Homologación y validá la conexión.',
    ],
    references: [
      {
        src: '/guides/arca-homologation/testing-01-wsass-home.png',
        alt: 'Referencia de ingreso a WSASS desde ARCA',
        caption: 'ARCA/Clave Fiscal → WSASS',
      },
      {
        src: '/guides/arca-homologation/testing-03-new-certificate.png',
        alt: 'Formulario Nuevo Certificado de WSASS',
        caption: 'Nuevo Certificado en WSASS',
      },
      {
        src: '/guides/arca-homologation/testing-04-create-authorization.png',
        alt: 'Selector de servicio wsfe en WSASS',
        caption: 'Autorización del servicio wsfe',
      },
    ],
  },
};

function ivaLabel(value: IvaCondition): string {
  switch (value) {
    case 'ResponsableInscripto':
      return 'Responsable Inscripto';
    case 'Monotributista':
      return 'Monotributista';
    case 'Exento':
      return 'Exento';
    case 'NoResponsable':
      return 'No Responsable';
    case 'ConsumidorFinal':
      return 'Consumidor Final';
    case 'SujetoNoCategorizado':
      return 'Sujeto No Categorizado';
    default:
      return value;
  }
}

function pointOfSaleGuidance(value: IvaCondition): string {
  if (value === 'Monotributista') {
    return 'En ARCA el punto de venta para Tilde debe usar Sistema “Factura Electrónica - Monotributo - Webservices”. Tilde emitirá comprobantes C.';
  }
  if (value === 'ResponsableInscripto') {
    return 'En ARCA el punto de venta para Tilde debe usar Sistema “RECE para aplicativo y web services”. Tilde resolverá comprobantes A o B según el receptor.';
  }
  return 'En ARCA elegí la opción de Factura Electrónica por Webservices habilitada para esta condición fiscal; evitá puntos “Factura en Línea” manuales.';
}

function environmentWarning(environment: PublicEnvironment): string {
  if (environment === 'Homologation') {
    return 'Este link pertenece al ambiente Homologación. El certificado y el permiso deben crearse en WSASS; un certificado productivo no valida en testing.';
  }

  return 'Este link pertenece al ambiente Producción. No lo uses en WSASS: producción y homologación tienen certificados, permisos y URLs separados.';
}

export const Route = createFileRoute('/certificates/csr')({
  validateSearch: (search: Record<string, unknown>): CsrPublicSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: PublicCsrDownloadPage,
});

function PublicCsrDownloadPage(): JSX.Element {
  const { token } = useSearch({ from: '/certificates/csr' });
  const [info, setInfo] = useState<certificatesApi.PublicCsrLinkInfoResponse | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState<string | null>(token ? null : 'El link no incluye un token válido.');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    certificatesApi
      .getPublicCsrLinkInfo(token)
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch((err) => {
        if (!cancelled) setError(toApiError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const download = (): void => {
    if (!token) return;
    window.location.assign(certificatesApi.publicCsrDownloadUrl(token));
  };

  const copyCsr = async (): Promise<void> => {
    if (!info?.csrPem) return;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(info.csrPem);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = info.csrPem;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f1e8', color: '#111827' }}>
      <Box sx={{ bgcolor: '#0b1020', color: '#fafaf5', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Stack spacing={2}>
            <Typography sx={{ fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase', color: TILDE_BRAND.citrus }}>
              tilde
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: 34, md: 48 }, fontWeight: 800, lineHeight: 1.04 }}>
              Descarga segura de CSR ARCA
            </Typography>
            <Typography sx={{ maxWidth: 620, color: 'rgba(250,250,245,0.72)', fontSize: 17, lineHeight: 1.6 }}>
              Tilde generó la solicitud pública para emitir el certificado. La clave privada queda
              protegida y cifrada en la plataforma.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
            <CircularProgress />
            <Typography color="text.secondary">Validando link…</Typography>
          </Stack>
        ) : null}

        {!loading && error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!loading && info ? (
          <Stack spacing={3}>
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                p: { xs: 2.5, md: 3 },
                boxShadow: '0 12px 40px rgba(15,23,42,0.08)',
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ color: '#573cff', fontWeight: 800, letterSpacing: '.16em' }}>
                    Solicitud lista
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                    {info.legalName}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                    CUIT {info.cuit}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.25 }}>
                    {ivaLabel(info.condicionIva)}
                  </Typography>
                </Box>
                <Chip
                  label={info.environment === 'Production' ? 'Producción' : 'Homologación'}
                  color={info.environment === 'Production' ? 'error' : 'info'}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>

              <Stack spacing={1.25} sx={{ mt: 3 }}>
                <Fact icon={<VerifiedUserOutlined />} text="El archivo es el CSR público requerido por ARCA." />
                <Fact icon={<LockOutlined />} text="La clave privada no viaja por email ni por este link." />
                <Fact icon={<Download />} text={`Disponible hasta ${new Date(info.expiresAt).toLocaleString('es-AR')}.`} />
              </Stack>
              <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2 }}>
                {environmentWarning(info.environment)}
              </Alert>
              <Alert severity="info" sx={{ mt: 1.25, borderRadius: 2 }}>
                {pointOfSaleGuidance(info.condicionIva)}
              </Alert>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 3 }}>
                {info.environment === 'Homologation' ? (
                  <Button
                    size="large"
                    variant="contained"
                    startIcon={<ContentCopy />}
                    onClick={() => void copyCsr()}
                    sx={{ px: 3, bgcolor: '#573cff', '&:hover': { bgcolor: '#4027e8' } }}
                  >
                    {copied ? 'CSR copiado' : 'Copiar CSR'}
                  </Button>
                ) : null}
                <Button
                  size="large"
                  variant={info.environment === 'Homologation' ? 'outlined' : 'contained'}
                  startIcon={<Download />}
                  onClick={download}
                  sx={
                    info.environment === 'Homologation'
                      ? { borderColor: '#573cff', color: '#573cff' }
                      : { px: 3, bgcolor: '#573cff', '&:hover': { bgcolor: '#4027e8' } }
                  }
                >
                  Descargar archivo CSR
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<PictureAsPdfOutlined />}
                  component="a"
                  href={csrWsfeManualUrl(info.environment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ borderColor: '#573cff', color: '#573cff' }}
                >
                  Ver manual Tilde
                </Button>
              </Stack>
            </Box>

            <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: { xs: 2.5, md: 3 } }}>
              <Typography variant="overline" sx={{ color: '#573cff', fontWeight: 800, letterSpacing: '.16em' }}>
                Instructivo público
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                {PUBLIC_GUIDES[info.environment].title}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                {PUBLIC_GUIDES[info.environment].intro}
              </Typography>
              <Box component="ol" sx={{ mt: 2, mb: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 1 }}>
                {PUBLIC_GUIDES[info.environment].steps.map((step, index) => (
                  <Box
                    component="li"
                    key={step}
                    sx={{ display: 'grid', gridTemplateColumns: '30px 1fr', gap: 1.25, alignItems: 'start' }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: '#573cff',
                        color: '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body2" sx={{ pt: 0.25, lineHeight: 1.55 }}>
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontWeight: 800, mb: 1.25 }}>Pantallas de referencia</Typography>
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                  {PUBLIC_GUIDES[info.environment].references.map((reference) => (
                    <Box
                      key={reference.src}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: '#fafaf8',
                      }}
                    >
                      <Box
                        component="img"
                        src={reference.src}
                        alt={reference.alt}
                        sx={{ display: 'block', width: '100%', height: 150, objectFit: 'contain', bgcolor: '#fff', p: 1 }}
                      />
                      <Typography variant="caption" sx={{ display: 'block', px: 1.25, py: 1, color: 'text.secondary' }}>
                        {reference.caption}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <Promo title="Certificados sin fricción" text="Generá CSR, importá el certificado y validá WSAA desde una misma operación guiada." />
              <Promo title="Operación fiscal centralizada" text="Tilde reúne CUITs, puntos de venta, comprobantes y alertas para equipos y estudios contables." />
            </Box>
          </Stack>
        ) : null}
      </Container>
    </Box>
  );
}

function Fact({ icon, text }: { icon: JSX.Element; text: string }): JSX.Element {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ color: '#573cff', display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Typography variant="body2">{text}</Typography>
    </Stack>
  );
}

function Promo({ title, text }: { title: string; text: string }): JSX.Element {
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.25 }}>
      <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
        {text}
      </Typography>
    </Box>
  );
}
